import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icons, toast } from "react-toastify";
import { useNotificationCenter } from "react-toastify/addons/use-notification-center";
import {
  selectActiveWinery,
  selectConfigs,
  selectIsLabelsLoaded,
  selectLabels,
  selectNotificationsUnread,
} from "../../../context/selectors";
import useLabels from "../../../hooks/useLabels";
import { Switch } from "../../FormComponents/Switch";
import { toggleNotificationsUnreadGrid } from "../../../context/configs/configsSlice";
import { CTA, Tooltip } from "@wine2trace/ui/components";
import "./styles/index.scss";
import "@wine2trace/ui/HeaderShell/styles";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { ROLES } from "../../../constants/base";
import useAuth from "../../../hooks/useAuth";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../routeAccess";
import { getOperationById, getOperations, markNotificationAsRead, markAllNotificationsAsRead as markAllNotificationsAsReadRequest, getChangelogStatus, markChangelogSeen } from "../../../services/utils";
import { socket } from "../../../socket";
import { useNavigate } from "react-router-dom";
import { add as addOp, updateNote, updateOp, removeOp, empty } from "../../../context/operations/operationsSlice";
import { removeExpOp } from "../../../context/expOperations/expOperationsSlice";
import { add as addLot, update as updateLot, removeL as removeLot } from "../../../context/lots/lotsSlice";
import pop from "../../../assets/sounds/pop.mp3"
import { addCompletedOp, removeCompletedOp } from "../../../context/completedOperations/completedOperationsSlice";
import { getItem } from "../../../services/LocalStorage";
import { Icon } from "../../../components/Icon";
import CacheManager from "../../../services/cacheManager";
import { processReplayNotifications } from "./notificationsReplay";
import { buildNotificationMessage } from "./buildNotificationMessage";
import { createLiveSyncScheduler } from "./liveSyncScheduler";
import { markAllNotificationsRead } from "./bulkMarkAllRead";
import { forActiveWinery, withWineryId } from "./notificationsForWinery";


const NOTIFICATION_INVALIDATION_MAP = {
  NEW_OPERATION: ["NEW_OPERATION"],
  UPDATE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  START_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "START_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  COMPLETE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  DELETE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  APPLY_WORKING_PROTOCOL: ["UPDATE_OPERATION"],
};

const NotificationsCenter = ({ isOpen, setIsOpen, setCounter }) => {
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeWinery = useSelector(selectActiveWinery);
  const configs = useSelector(selectConfigs);
  const isLabelsLoaded = useSelector(selectIsLabelsLoaded);
  const showUnreadOnly = useSelector(selectNotificationsUnread);

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const { can } = usePermissions();
  // The daily report toast counts the operations completed today (GET /operations): OPERATIONS read.
  const canReadOperations = can(...PERMISSIONS.operationsRead);
  const seenNotificationsRef = useRef(new Set());
  // Single stable CacheManager for the component's lifetime (rule 7): never
  // instantiate a new one per notification/operation.
  const cacheManagerRef = useRef(null);
  if (!cacheManagerRef.current) {
    cacheManagerRef.current = new CacheManager(dispatch, axiosPrivate);
  }
  const liveSyncSchedulerRef = useRef(null);

  const {
    notifications,
    markAsRead,
    remove,
    add,
  } = useNotificationCenter({
    filter: (item) => item.data.exclude === false,
  });

  // The inbox outlives a winery switch: bell and counter only deal with the active winery's entries.
  const visibleNotifications = forActiveWinery(notifications, activeWinery?.id);
  const unreadCount = visibleNotifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (!isLabelsLoaded) return;

    // Attach listener immediately when component mounts
    socket.on("notifications", onNotifications);

    return () => {
      // Cleanup listener on unmount
      socket.off("notifications", onNotifications);
    };
  }, [isLabelsLoaded, activeWinery?.id, canReadOperations]); // onNotifications filters by the active winery and gates the daily report on the grants: re-bind when they change

  // Historical/backlog replay on mount or reconnect ("notifications:replay"):
  // purely additive to the inbox, no domain side effects (no fetch/dispatch,
  // no sound, no toast) - see notificationsReplay.js. Kept on its own
  // listener so it can never be confused with, or alter, the live path above.
  useEffect(() => {
    if (!isLabelsLoaded) return;

    socket.on("notifications:replay", onNotificationsReplay);

    return () => {
      socket.off("notifications:replay", onNotificationsReplay);
    };
  }, [isLabelsLoaded, activeWinery]);

  // Coalescing scheduler lifecycle (rule 8): stop it once on unmount so no
  // pending timer can flush - and no side effect can fire - after that.
  useEffect(() => {
    return () => {
      liveSyncSchedulerRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (!isLabelsLoaded) return;

    const checkChangelogStatus = async () => {
      const { latestId, lastSeenId } = await getChangelogStatus(axiosPrivate);
      if (latestId > lastSeenId) {
        add({
          id: `changelog-${latestId}`,
          title: getLabel("changelogNotifTitle"),
          content: getLabel("changelogNotifContent"),
          date: new Date().toISOString(),
          data: { exclude: false, isChangelog: true, latestId },
        });
      }
    };

    checkChangelogStatus();
  }, [isLabelsLoaded]);

  const removeOnCondition = (predicate) => {
    notifications
      ?.filter(predicate)
      ?.forEach((n) => remove(n.id));
  };

  const invalidateOldNotifications = (incomingNotification) => {
    const { type, data } = incomingNotification;
    const operationId = data?.operation_id;

    if (!operationId) return;

    const typesToInvalidate = NOTIFICATION_INVALIDATION_MAP[type];
    if (!typesToInvalidate) return;

    removeOnCondition((n) =>
      n?.data?.operation_id === operationId &&
      typesToInvalidate.includes(n?.type)
    );
  };

  const handleNotifications = (notification) => {
    // 1️⃣ deduplica hard (id)
    if (seenNotificationsRef.current.has(notification.id)) return;
    seenNotificationsRef.current.add(notification.id);

    // 2️⃣ invalida notifiche obsolete
    invalidateOldNotifications(notification);

    // 3️⃣ i side effect di dominio (fetch operation/batch, reload protocol
    // applications) sono differiti e coalesciati in finestre di 250ms -
    // vedi liveSyncScheduler.js - non avvengono più qui in modo sincrono.
    liveSyncSchedulerRef.current?.schedule(notification);

    const isLink =
      notification?.data?.operation_id &&
      notification?.data?.operation_type &&
      // (notification.type === "NOTE", "NEW_OPERATION" || notification.type === "UPDATE_OPERATION")
      ["NOTE", "NEW_OPERATION", "UPDATE_OPERATION", "START_OPERATION", "DELETE_OPERATION", "COMPLETE_SAMPLING"].includes(notification?.type)
        ? () => {
            markAsRead(notification.id);
            navigate(
              `/programming/${notification?.data?.operation_type?.toLowerCase?.()}/${notification?.data?.operation_id}`
            );
            setIsOpen?.(false);
          }
        : null;

    const content = buildToastMessage(notification);

    if (content) {
      const shouldShowPopup = localStorage.getItem('toastNotifications') === 'toastStatus_ON';
      // 1) sempre aggiungo alla notification center (fa aumentare la campanella)

      add({
        id: notification?.id ?? `notif_${Date.now()}`,
        title: notification?.title ?? '',
        content: content,
        date: new Date().toISOString(),
        data: withWineryId(notification, { exclude: !shouldShowPopup })
      });

      // 2) solo se la user wants popups
      if (shouldShowPopup) {
        toast.info(content, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: notification?.id,
          ...(isLink && { onClick: isLink }),
        });
      }
    }
  }

  const onNotifications = (dataArray) => {
    console.log('dataArray', dataArray)
    if (!Array.isArray(dataArray) || dataArray.length === 0) return;
    if (!isLabelsLoaded) return;

    const data = dataArray.filter(
      d => {
        // winery_id can be at top-level OR inside data object
        const wineryId = d?.winery_id || d?.data?.winery_id;
        return Number(wineryId) === Number(activeWinery?.id);
      }
    );

    if (data.length === 0) return;

    playNotificationSound();

    let index = 0;

    const processNextNotification = () => {
      if (index >= data.length) return;

      handleNotifications(data[index]);
      index++;

      setTimeout(processNextNotification, 100);
    };

    processNextNotification();
  };

  const onNotificationsReplay = (dataArray) => {
    processReplayNotifications(dataArray, {
      activeWineryId: activeWinery?.id,
      seenIds: seenNotificationsRef.current,
      add,
      // Pure text resolver only (no isDailyReport side effect): replay must
      // never trigger fetch/dispatch/sound/toast, see notificationsReplay.js.
      buildMessage: (raw) => buildNotificationMessage(raw, { getLabel, configs, role }),
    });
  };

  const playNotificationSound = () => {
    const audio = new Audio(pop);
    audio.play().catch((e) => console.error("Audio failed", e));
  };

  const normalizeChatNotesTimestamps = (op) => {
    return {
      ...op,
      chat_notes: op.chat_notes?.map((note) => ({
        ...note,
        timestamp:
          typeof note.timestamp === "string"
            ? note.timestamp
            : note.timestamp?.toISOString?.() ?? note.timestamp,
      })),
    };
  };

  // Delegates to CacheManager.fetchProtocolApplications(), which does its own
  // raw GET and only dispatches on success -- unlike getProtocolApplications()
  // in services/utils.js, it never dispatches [] on failure (that would wipe
  // Redux instead of leaving it untouched). liveSyncScheduler's flush() already
  // wraps this call in its own try/catch and logs failures, so none is needed
  // here.
  const reloadProtocolApplications = () => cacheManagerRef.current.fetchProtocolApplications();

  // Le operazioni push via websocket non passano da affected_entities: i tank
  // src/dest coinvolti restano stale in Redux (planned_quantity, is_planned, ecc.
  // sono calcolati backend-side) finché non li rinfreschiamo esplicitamente qui,
  // come fa CacheManager.processAffectedEntities per le operazioni create in locale.
  // Riusa la CacheManager singola del componente (rule 7), non ne crea una per chiamata.
  const refreshOperationTanks = (op) => {
    const tankIds = new Set([
      ...(op?.src_tanks?.map((t) => t?.tank_id) || []),
      ...(op?.dest_tanks?.map((t) => t?.tank_id) || []),
    ].filter(Boolean));

    if (tankIds.size === 0) return;

    cacheManagerRef.current.processTanks(
      [...tankIds].map((id) => ({ id, action: "update" }))
    );
  };

  const fetchOperationForSync = async (operationId) => {
    const res = await getOperationById(axiosPrivate, operationId);
    return Array.isArray(res) && res.length > 0 ? res[0] : null;
  };

  const fetchBatchForSync = async (batchId) => {
    const response = await axiosPrivate.get(`/batches/${batchId}`);
    return Array.isArray(response?.data) ? response.data[0] : response?.data ?? null;
  };

  // Applies the fetched operation once per coalescing window (see
  // liveSyncScheduler.js). Same per-type dispatch semantics as before N4;
  // DELETE_OPERATION never reaches here (handled by dispatchOperationRemoval).
  const dispatchOperationUpdate = (op, type, notification) => {
    switch (type) {
      case "NOTE":
        dispatch(updateNote(normalizeChatNotesTimestamps(op)));
        break;
      case "NEW_OPERATION":
        dispatch(addOp(op));
        break;
      case "UPDATE_OPERATION":
      case "START_OPERATION":
      case "COMPLETE_SAMPLING":
        dispatch(updateOp(op));
        break;
      case "COMPLETE_OPERATION": {
        dispatch(addCompletedOp(op));
        const idsToRemove = notification?.data?.deleted_operation_ids?.length
          ? notification.data.deleted_operation_ids
          : [op.id];
        idsToRemove.forEach((deleteId) => {
          dispatch(removeOp({ id: deleteId }));
          dispatch(removeExpOp({ id: deleteId }));
        });
        break;
      }
      case "APPLY_WORKING_PROTOCOL":
      case "APPLY_WORKING_PROTOCOL_POST":
      case "APPLY_FILTERING":
      case "APPLY_STABILIZATION":
        dispatch(addOp(op));
        break;
      default:
        break;
    }
  };

  // DELETE_OPERATION: idempotent removal across all three slices, never a
  // fetch (rule 6).
  const dispatchOperationRemoval = (id) => {
    dispatch(removeOp({ id }));
    dispatch(removeExpOp({ id }));
    dispatch(removeCompletedOp({ id }));
  };

  const dispatchBatchUpdate = (batchData, type) => {
    if (type === "NEW_BATCH") {
      dispatch(addLot(batchData));
    } else {
      try {
        dispatch(updateLot(batchData));
      } catch {
        dispatch(addLot(batchData));
      }
    }
  };

  // Batch no longer in the list scope (fetch answered 404): drop the stale entry.
  const dispatchBatchRemoval = (id) => {
    dispatch(removeLot({ id }));
  };

  if (!liveSyncSchedulerRef.current) {
    liveSyncSchedulerRef.current = createLiveSyncScheduler({
      fetchOperation: fetchOperationForSync,
      fetchBatch: fetchBatchForSync,
      reloadProtocolApplications,
      dispatchOperationUpdate,
      dispatchOperationRemoval,
      dispatchBatchUpdate,
      dispatchBatchRemoval,
      refreshOperationTanks,
    });
  }

  // Text resolution itself lives in buildNotificationMessage (shared with the
  // replay path in notificationsReplay.js); this wrapper only adds the
  // DAILY_REPORT side effect, which must never fire during replay.
  const buildToastMessage = (notification) => {
    if (notification?.type === "DAILY_REPORT") {
      if (canReadOperations) isDailyReport();
      return null;
    }

    return buildNotificationMessage(notification, { getLabel, configs, role });
  };

  // Daily report: what was completed today, for who may read the operations
  const isDailyReport = async () => {
    if (canReadOperations) {
      const startTime = new Date();
      startTime.setHours(0, 0, 0, 0);
      const completedOps =
        (await getOperations(axiosPrivate, true, null, startTime)) || [];
      if (completedOps?.length > 0) {
        const customIdDailyReport = "custom-id-daily-report";
        toast(getLabel("toast_dailyReport"), {
          toastId: customIdDailyReport,
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          icon: Icons.success({
            theme: "light",
            type: toast.TYPE.SUCCESS,
          }),
          position: toast.POSITION.BOTTOM_RIGHT,
          data: {
            exclude: false,
          },
        });
      }
    }
  };

  // Update unread counter when notifications change
  useEffect(() => {
    setCounter(unreadCount);
  }, [unreadCount, visibleNotifications.length]);


  // Toggle unread-only mode
  const toggleDisplayMode = () => {
    dispatch(toggleNotificationsUnreadGrid());
  };

  const readNotification = async (notification, isRemove = false) => {
    if (notification?.data?.isChangelog) {
      try {
        await markChangelogSeen(notification.data.latestId, axiosPrivate);
      } catch (err) {
        console.log('err', err)
      }
      if (isRemove) {
        remove(notification.id);
      } else {
        markAsRead(notification.id);
      }
      return;
    }

    try {
      const response = await markNotificationAsRead(
        notification?.id,
        axiosPrivate
      );

      if (response?.success) {
        if (isRemove) {
          remove(notification.id);
        } else {
          markAsRead(notification.id);
        }
      }
    } catch (err) {
      console.log('err', err)
    }
  };

  const deleteAll = async () => {
    try {
      await markAllNotificationsRead({
        notifications: visibleNotifications,
        axiosPrivate,
        markAllNotificationsAsReadRequest,
        markChangelogSeen,
        // The server only touches the active winery's notifications: so does the local inbox.
        applyLocalUpdate: () => remove(visibleNotifications.map((n) => n.id)),
      });
    } catch (err) {
      console.log('err', err);
    }
  }

  const markAllRead = async () => {
    try {
      await markAllNotificationsRead({
        notifications: visibleNotifications,
        axiosPrivate,
        markAllNotificationsAsReadRequest,
        markChangelogSeen,
        applyLocalUpdate: () => markAsRead(visibleNotifications.map((n) => n.id)),
      });
    } catch (err) {
      console.log('err', err);
    }
  }

  return (
    <>
      {isOpen && (
        <aside onClick={(e) => e.stopPropagation()} className="motion-aside-container">
          <header className="header">
            <h3>{getLabel("notifications")}</h3>
            <div className="unread-filter">
              <Switch
                negative={true}
                leftOption={{
                  label: showUnreadOnly
                    ? getLabel("notificationsShowUnread")
                    : getLabel("notificationsShowAll"),
                }}
                rightOption={{
                  label: "",
                }}
                toggleHandler={toggleDisplayMode}
                status={showUnreadOnly}
              />
            </div>
          </header>
          <section className="motion-section-content">
            {(!visibleNotifications.length ||
              (unreadCount === 0 && showUnreadOnly)) && (
              <h4>{getLabel("notificationsNoItems")}</h4>
            )}
            {(showUnreadOnly
              ? visibleNotifications.filter((v) => !v.read)
              : visibleNotifications
            ).map((notification) => (
              <div key={notification.id} className="motion-item">
                <article className="motion-article-item">
                  <div className="icon-wrapper">
                    {notification.icon ||
                      Icons.info({
                        theme: notification.theme || "light",
                        type: toast.TYPE.INFO,
                      })}
                  </div>
                  <div
                    className="content"
                    onClick={
                      notification?.data?.isChangelog
                        ? () => {
                            readNotification(notification);
                            navigate("/changelog");
                            setIsOpen?.(false);
                          }
                        : undefined
                    }
                    style={notification?.data?.isChangelog ? { cursor: "pointer" } : undefined}
                  >
                    <div>{notification.content}</div>
                  </div>
                  <div className="tools">
                    {(
                      // (notification?.data?.note_type === "NOTE" ||
                      // notification?.data?.note_type === "NEW_OPERATION") && 
                      notification?.read === true) ? (
                      <Icon                             size={"lg"}
                        icon="fa-check"
                        style={{ color: "#4bbd7f" }}
                      />
                    ) : (
                      <Tooltip
                        variant={"info"}
                        html={getLabel("notificationsMarkAsRead")}
                        place="left"
                        events={["hover"]}
                      >
                        <div
                          className="pulsating-dot"
                          onClick={() => readNotification(notification)}
                        >
                          <div className={`circle ${notification.type}`}>
                            <div
                              className={`ring ${notification.type} `}
                            ></div>
                          </div>
                        </div>
                      </Tooltip>
                    )}
                    <Tooltip
                      variant={"info"}
                      html={getLabel("notificationsMarkAsRemoved")}
                      place="left"
                      events={["hover"]}
                    >
                      <div
                        className="remove"
                        onClick={() =>
                          readNotification(notification, true)
                        }
                      >
                        <Icon                               icon="fa-remove"
                          style={{ color: "#bb4456" }}
                          size={"lg"}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </article>
              </div>
            ))}
          </section>
          <footer className="footer">
            <div className="footer_wrapper">
              <CTA isFull={true} onClick={deleteAll}>
                {getLabel("notificationsClearAll")}
              </CTA>
              <CTA isFull={true} onClick={markAllRead}>
                {getLabel("notificationsMarkAll")}
              </CTA>
            </div>
          </footer>
        </aside>
      )}
    </>
  );
};

export default NotificationsCenter;
